import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportHorizontalBarChart,
  ReportMultiSeriesChart,
  TONE_COLOURS,
  CHART_COLOURS
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {humanise} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * ORD1 — Order Volume & Fulfilment.
 *
 * The operational twin of Materials Revenue: same orders, but counts and days
 * instead of pounds. Stacked status bars show the pipeline over time; the
 * lead-time distribution answers "how fast".
 *
 * The "Before order date" band is not a mistake — two dispatched orders carry a
 * dispatch date earlier than their order date. Showing them beats hiding them.
 */

const SLUG = 'order-volume';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const STATUS_OPTIONS = [
  {value: 'dispatched', label: 'Dispatched'},
  {value: 'sent', label: 'Sent to Print Partner'},
  {value: 'cancelled', label: 'Cancelled'}
];

const STATUS_BADGE: Record<string, string> = {
  // Based on the Material Orders list (order-form/ListOrders.tsx), except that
  // "Sent to Print Partner" is amber here so it stands apart from Dispatched.
  dispatched: 'success',
  sent: 'warning',
  acknowledged: 'success',
  unpaid: 'warning',
  cancelled: 'danger'
};

const OrderVolume = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Order Volume', {
    omitSeriesKeys: ['acknowledged'],
    // Same wording as the Orders table.
    headers: {payment_source: 'Type'},
    values: {order_status: {sent: 'Sent to Print Partner'}}
  });

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byPeriod = collections?.series?.by_period || [];
  const byLeadTime = collections?.series?.by_lead_time || [];
  const detail = collections?.detail;

  const columns = [
    {
      name: 'Order',
      selector: (row: any) => row.order_name || `#${row.id}`,
      // An order reference is short; a fixed width stops it taking a flexible
      // share of the row and leaving a wide gap before Franchise.
      width: '110px',
      wrap: true
    },
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {
      name: 'Status',
      width: '180px',
      cell: (row: any) => (
        <span className={`badge badge-${STATUS_BADGE[row.order_status] || 'danger'}`}>
          {STATUS_OPTIONS.find((o) => o.value === row.order_status)?.label ||
            humanise(row.order_status)}
        </span>
      )
    },
    {
      name: 'Type',
      width: '120px',
      cell: (row: any) =>
        row.payment_source ? (
          <span className="badge badge-secondary">{row.payment_source}</span>
        ) : (
          '---'
        )
    },
    {name: 'Placed', selector: (row: any) => row.placed_on || '---', width: '115px'},
    {name: 'Dispatched', selector: (row: any) => row.dispatched_on || '---', width: '120px'},
    {
      name: 'Days',
      width: '100px',
      right: true,
      cell: (row: any) => {
        const d = row.days_to_dispatch;
        if (d === null || d === undefined) return '---';
        const cls = d < 0 ? 'text-danger font-weight-bold' : d > 7 ? 'text-warning' : '';
        return <span className={cls}>{d}</span>;
      }
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Order Volume & Fulfilment'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Order Volume</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, orderStatus: true, paymentSource: true}}
            orderStatusOptions={STATUS_OPTIONS}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-7">
              <ChartCard
                title="Orders over time, by status"
                subtitle="Stacked, so the whole pipeline is visible in one bar per period"
                loading={loading}
                isEmpty={!byPeriod.length}>
                <ReportMultiSeriesChart
                  data={byPeriod}
                  xKey="bucket"
                  currency={false}
                  series={[
                    {key: 'dispatched', name: 'Dispatched', stackId: 'o', colour: '#4E8C4A'},
                    {key: 'sent', name: 'Sent to Print Partner', stackId: 'o', colour: CHART_COLOURS[1]},
                    {key: 'cancelled', name: 'Cancelled', stackId: 'o', colour: '#B04A5A'}
                  ]}
                />
              </ChartCard>
            </div>
            <div className="col-lg-5">
              <ChartCard
                title="How fast orders ship"
                subtitle="Days from order to dispatch"
                loading={loading}
                isEmpty={!byLeadTime.length}>
                <ReportHorizontalBarChart
                  data={byLeadTime}
                  labelKey="band"
                  valueKey="orders"
                  currency={false}
                  labelWidth={130}
                  colourField="tone"
                  colourMap={TONE_COLOURS}
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Orders</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No orders match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrderVolume;
